import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import ArrowBackwardButton from '../../ArrowBackwardButton';
import ArrowForwardButton from '../../ArrowForwardButton';

import NextButton from './NextButton';

const DIRECTION = {
  FORWARD: 'FORWARD',
  BACKWARD: 'BACKWARD',
};
const NEXT_BUTTON_BOTTOM = 33;
const useStyles = makeStyles((theme) => ({
  mainNotProvider: {
    height: 40,
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: '#f8f8f8',
  },
  rootNotProvider: {
    maxWidth: 893,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  root: {
    // backgroundColor: '#fff',
    width: '100%',
    height: ({ provider }) => (provider ? 45 : 'unset'),
    position: ({ provider }) => (provider ? 'unset' : 'absolute'),
    bottom: ({ provider }) => (provider ? 'unset' : '0px !important'),
    paddingTop: ({ provider }) => (provider ? 5 : 0),
    touchAction: 'manipulation',
  },
  nextButton: ({ provider }) => ({
    position: provider ? 'unset !important' : 'absolute',
    textAlign: 'center',
    width: '100%',
    paddingLeft: '5px',
    paddingRight: '5px',
    boxSizing: 'border-box',
    bottom: '33px !important',
    '& button': {
      fontSize: provider ? '16px' : 'unset',
      borderRadius: provider ? '14px' : 'unset',
    },
  }),
}));

/**
 * Bottom navigation for the questionnaire
 */
const QuestionnaireNavigator = (props) => {
  const rootEl = useRef(null);
  const nextButtonEl = useRef(null);
  const { appConfiguration: { providerSpecific } = {} } = useSelector((state) => state) ?? {};
  const classes = useStyles({ provider: providerSpecific?.code });
  const {
    handleSkipButton,
    handlePreviousButton,
    disablePreviousButton,
    disableSkipButton,
    shouldNextButtonBeShown,
    shouldSkipButtonBeShown,
  } = props;

  const isMobile = /Android|iPhone/i.test(navigator.userAgent);
  const isProvider = !!providerSpecific?.code;
  let providerHeight = null;
  const providerRootEl = document.querySelector('#rootContainer');
  if (isProvider && providerRootEl) {
    providerHeight = (providerRootEl.clientHeight - 20) + 'px';
  }
  useEffect(() => {
    if (isMobile) {
      let navigationBottom = null;
      const viewport = window.visualViewport;
      let height = window.visualViewport.height;
      const focusHandler = () => {
        if (navigationBottom) {
          if (rootEl.current) {
            rootEl.current.style.bottom = navigationBottom;
          }
          if (nextButtonEl.current) {
            nextButtonEl.current.style.bottom = parseInt(navigationBottom) + parseInt(NEXT_BUTTON_BOTTOM) + 'px';
          }
        }
      };
      const blurHandler = () => {
        if (isProvider) {
          providerRootEl.style.height = providerHeight;
        } else {
          rootEl.current.style.bottom = 0;
          if (nextButtonEl && nextButtonEl.current) {
            nextButtonEl.current.style.bottom = NEXT_BUTTON_BOTTOM + 'px';
          }
        }
      };
      document.querySelectorAll('input').forEach(input => input.addEventListener('blur', blurHandler));
      document.querySelectorAll('input').forEach(input => input.addEventListener('focus', focusHandler));
      const resizeHandler = () => {
        if (isProvider) {
          if (height - viewport.height) {
            providerRootEl.style.height = `${height - viewport.height}px`;
          } else {
            providerRootEl.style.height = providerHeight;
          }
        } else {
          if (rootEl.current) {
            rootEl.current.style.bottom = `${height - viewport.height}px`;
            navigationBottom = rootEl.current.style.bottom;
            if (nextButtonEl && nextButtonEl.current) {
              nextButtonEl.current.style.bottom = parseInt(navigationBottom) + parseInt(NEXT_BUTTON_BOTTOM) + 'px';
            }
          }
        }
      };
      window.visualViewport.addEventListener('resize', resizeHandler);
      return () => {
        document.querySelectorAll('input').forEach(input => input.removeEventListener('focus', focusHandler));
        document.querySelectorAll('input').forEach(input => input.removeEventListener('blur', blurHandler));
        window.visualViewport.removeEventListener('resize', resizeHandler);
      };
    }
  }, [isMobile, isProvider, providerRootEl, providerHeight]);

  const handleChange = (direction) => () => {
    if (direction === DIRECTION.FORWARD) {
      // return dispatch(goToNextQuestion())
      return handleSkipButton();
    }

    // return dispatch(handlePreviousButton())
    return handlePreviousButton();
  };

  const nextButton = () => (
    <div className={classes.nextButton} ref={nextButtonEl} style={{
      bottom: isMobile && rootEl.current ? parseInt(rootEl.current.style.bottom) + NEXT_BUTTON_BOTTOM + 'px' : NEXT_BUTTON_BOTTOM + 'px',
    }}>
      <NextButton />
    </div>
  );

  return (
    <div className={!isProvider ? classes.mainNotProvider : ''}>
      { shouldNextButtonBeShown && !providerSpecific?.code ? nextButton() : null}
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems={providerSpecific?.code ? 'center' : 'flex-start'}
        className={cx(classes.root, { [classes.rootNotProvider]: !isProvider })}
        ref={rootEl}
      >
        <Grid item>
          <ArrowBackwardButton
            label="questionnaire.button.previous"
            onClick={handleChange(DIRECTION.BACKWARD)}
            isDisabled={disablePreviousButton}
          />
        </Grid>
        {providerSpecific?.code && shouldNextButtonBeShown && (
          <Grid item>
            {nextButton()}
          </Grid>
        )}
        {shouldSkipButtonBeShown && (
          <Grid item>
            <ArrowForwardButton
              label="questionnaire.button.skip"
              onClick={handleChange(DIRECTION.FORWARD)}
              isDisabled={disableSkipButton}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

QuestionnaireNavigator.propTypes = {
  shouldNextButtonBeShown: PropTypes.bool.isRequired,
  shouldSkipButtonBeShown: PropTypes.bool.isRequired,
  handleSkipButton: PropTypes.func,
  handlePreviousButton: PropTypes.func,
  disablePreviousButton: PropTypes.bool,
  disableSkipButton: PropTypes.bool,
};

export default QuestionnaireNavigator;
